import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock } from "@fortawesome/free-solid-svg-icons";
import { CustomModal, ModernButton } from "./CustomModal";
import { isCompletoTier } from "../utils/planFeatures";

const CustomSelectWrap = styled.div`
  max-width: 100%;
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  justify-content: ${({
  $align
}) => $align === "start" ? "flex-start" : "center"};
  align-items: center;
`;
const CustomSelectOption = styled.div`
  /* Lines up with the date and time fields it sits beside on the restaurant's
     one-page form. Height as well as min-height: the floor alone still lets a
     long label push the chip taller than the row it belongs to. */
  height: ${({
  $fromRestaurant
}) => $fromRestaurant ? "32px" : "auto"};
  min-height: ${({
  $fromRestaurant
}) => $fromRestaurant ? "32px" : "30px"};
  display: inline-flex;
  align-items: center;
  position: relative;
  padding: 5px 8px;
  font-size: 13px;
  border-radius: 7px;
  margin: 0;

  cursor: ${({
  $isFull,
  $fromRestaurant
}) => $isFull && !$fromRestaurant ? "default" : "pointer"};

  pointer-events: ${({
  $isFull,
  $fromRestaurant
}) => $isFull && !$fromRestaurant ? "none" : "auto"};

  border: 1px solid
    ${({
  $isSelected,
  $isFull,
  $fromRestaurant,
  $widgetDark
}) => {
  if ($isFull && !$fromRestaurant) {
    return "#d94b4b";
  }
  if ($isSelected) {
    return "var(--widget-main-color, #99b0ad)";
  }
  return $widgetDark ? "rgba(246, 241, 232, 0.28)" : "rgba(153, 176, 173, 0.75)";
}};

  background-color: ${({
  $isSelected,
  $isFull,
  $fromRestaurant,
  $widgetDark
}) => {
  if ($isFull && !$fromRestaurant) {
    return "#d94b4b";
  }
  if ($isSelected) {
    return "var(--widget-main-color, #99b0ad)";
  }
  return $widgetDark ? "rgba(255, 255, 255, 0.06)" : "transparent";
}};

  color: ${({
  $isSelected,
  $isFull,
  $fromRestaurant,
  $widgetDark
}) => {
  if ($isFull && !$fromRestaurant) {
    return "#ffffff";
  }
  if ($isSelected) {
    return "#ffffff";
  }
  return $widgetDark ? "#f6f1e8" : "#343a38";
}};

  box-sizing: border-box;

  transition:
    background-color 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease,
    transform 0.15s ease;

  &:hover {
    border-color: ${({
  $isFull,
  $fromRestaurant
}) => $isFull && !$fromRestaurant ? "#d94b4b" : "var(--widget-main-color, #99b0ad)"};

    background-color: ${({
  $isSelected,
  $isFull,
  $fromRestaurant,
  $widgetDark
}) => {
  if ($isFull && !$fromRestaurant) {
    return "#d94b4b";
  }
  if ($isSelected) {
    return "var(--widget-main-color, #99b0ad)";
  }
  return $widgetDark ? "rgba(255, 255, 255, 0.11)" : "rgba(153, 176, 173, 0.1)";
}};

    transform: ${({
  $isFull,
  $fromRestaurant
}) => $isFull && !$fromRestaurant ? "none" : "translateY(-1px)"};
  }
`;
const CustomSelectText = styled.span`
  min-width: 0;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  line-height: 1.15;
  white-space: nowrap;
`;
const FullText = styled.span`
  color: #ffffff;
  white-space: nowrap;
`;
const PlanLock = styled(FontAwesomeIcon)`
  color: #ee754c;
  font-size: 12px;
  flex: 0 0 auto;
`;
const ModalContent = styled.div`
  padding: 8px 2px;
`;
const ModalText = styled.p`
  margin: 0 0 18px;
  color: #343a38;
`;
const ModalButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 10px;
`;
/* Two mutually exclusive modes read as one pill rather than two loose chips,
   and a segment cannot be clicked off the way a chip can: there is no such
   thing as a booking that is neither a reservation nor a door queue. */
const SegmentedWrap = styled.div`
  display: inline-flex;
  max-width: 100%;
  padding: 2px;
  border-radius: 999px;
  background: #f4f7f6;
  box-shadow: inset 0 0 0 1px rgba(153, 176, 173, 0.3);
`;
const SegmentedOption = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 26px;
  padding: 0 12px;
  border: 0;
  border-radius: 999px;
  white-space: nowrap;
  cursor: pointer;
  font-size: 11.5px;
  font-weight: 700;

  background-color: ${({
  $isSelected
}) => $isSelected ? "var(--app-orange, #ee754c)" : "transparent"};

  color: ${({
  $isSelected
}) => $isSelected ? "#ffffff" : "#6f7c79"};

  transition:
    background-color 0.14s ease,
    color 0.14s ease;

  &:hover {
    color: ${({
  $isSelected
}) => $isSelected ? "#ffffff" : "var(--app-orange, #ee754c)"};
  }
`;
const getLabel = (item, t) => {
  const itemString = String(item || "");
  if (itemString === "Lunch" || itemString === "Lunch/f") {
    return t("ui.i18nFinalCleanup.lunch");
  }
  if (itemString === "Dinner" || itemString === "Dinner/f") {
    return t("ui.i18nFinalCleanup.dinner");
  }
  if (itemString === "Breakfast" || itemString === "Breakfast/f") {
    return t("ui.i18nFinalCleanup.breakfast");
  }
  if (itemString === "Cadeira de rodas") {
    return t("ui.i18nFinalCleanup.wheelchair");
  }
  if (itemString === "Não precisa") {
    return t("ui.i18nFinalCleanup.notNeeded");
  }
  if (itemString === "reservation") {
    return t("ui.i18nFinalCleanup.reservation");
  }
  if (itemString === "waiting_list") {
    return t("ui.i18nFinalCleanup.doorQueue");
  }
  if (itemString.endsWith("/f")) {
    return itemString.slice(0, -2);
  }
  return itemString;
};
function CustomSelect({
  items,
  onSelectChange,
  selectedItem,
  getBorderColor,
  setUpdate = null,
  from_restaurant = false,
  widgetDark = false,
  sub_lvl = null,
  restaurant_id = null,
  // "segmented" is the two-mode pill; it never allows an empty selection.
  variant = "chips",
  align = "center"
}) {
  const {
    t
  } = useTranslation();
  const navigate = useNavigate();
  const [showWaitingListUpgradeModal, setShowWaitingListUpgradeModal] = useState(false);
  const hasSubscriptionLevel = sub_lvl !== null && sub_lvl !== undefined && sub_lvl !== "";
  const isWaitingListBlockedForPlan = hasSubscriptionLevel && !isCompletoTier(sub_lvl);
  const handleItemClick = item => {
    const itemString = String(item || "");
    const isBlockedWaitingList = itemString === "waiting_list" && isWaitingListBlockedForPlan;
    if (isBlockedWaitingList) {
      setShowWaitingListUpgradeModal(true);
      return;
    }
    const nextValue = variant === "segmented" ? item : selectedItem === item ? "" : item;
    onSelectChange(nextValue);
    if (typeof setUpdate === "function") {
      setUpdate(previousValue => typeof previousValue === "number" ? previousValue + 1 : 1);
    }
  };
  const handleViewPlans = () => {
    setShowWaitingListUpgradeModal(false);
    if (restaurant_id !== null && restaurant_id !== undefined) {
      navigate(`/pick_plan_and_pay/${restaurant_id}`);
    }
  };
  const renderSegmented = () => <SegmentedWrap>
      {items?.map((item, index) => {
      const itemString = String(item || "");
      const isWaitingListBlocked = itemString === "waiting_list" && isWaitingListBlockedForPlan;
      const isSelected = !isWaitingListBlocked && selectedItem === item;
      return <SegmentedOption key={`${itemString}-${index}`} type="button" $isSelected={isSelected} onClick={() => handleItemClick(item)} aria-pressed={isSelected} aria-haspopup={isWaitingListBlocked ? "dialog" : undefined} title={isWaitingListBlocked ? t("ui.i18nCleanup2.verPlanosComAcessoAFilaAPorta") : undefined}>
            {getLabel(itemString, t)}

            {isWaitingListBlocked && <PlanLock icon={faLock} />}
          </SegmentedOption>;
    })}
    </SegmentedWrap>;
  return <>
      {variant === "segmented" ? renderSegmented() : <CustomSelectWrap $align={align}>
        {items?.map((item, index) => {
        const itemString = String(item || "");
        const isFull = itemString.endsWith("/f");
        const isWaitingListBlocked = itemString === "waiting_list" && isWaitingListBlockedForPlan;
        const isSelected = !isWaitingListBlocked && selectedItem === item;
        return <CustomSelectOption key={`${itemString}-${index}`} id={`custom-select-option-${index}`} $isSelected={isSelected} $isFull={isFull} $fromRestaurant={from_restaurant} $widgetDark={widgetDark} onClick={() => handleItemClick(item)} aria-haspopup={isWaitingListBlocked ? "dialog" : undefined} title={isWaitingListBlocked ? t("ui.i18nCleanup2.verPlanosComAcessoAFilaAPorta") : undefined} style={{
          borderColor: !isWaitingListBlocked && getBorderColor ? getBorderColor(item) : undefined
        }}>
              <CustomSelectText>
                <span>{getLabel(itemString, t)}</span>

                {isWaitingListBlocked && <PlanLock icon={faLock} />}

                {isFull && !from_restaurant && <FullText>{t("ui.components.selectableCustom.lotado")}</FullText>}
              </CustomSelectText>
            </CustomSelectOption>;
      })}
      </CustomSelectWrap>}

      <CustomModal show={showWaitingListUpgradeModal} onHide={() => setShowWaitingListUpgradeModal(false)} title={t("ui.components.selectableCustom.filaAPorta")}>
        <ModalContent>
          <ModalText>{t("ui.components.selectableCustom.aCriacaoDeFilaAPorta")}</ModalText>

          <ModalButtons>
            <ModernButton color="#99b0ad" onClick={() => setShowWaitingListUpgradeModal(false)}>{t("ui.components.selectableCustom.agoraNao")}</ModernButton>

            <ModernButton color="#ee754c" onClick={handleViewPlans} disabled={restaurant_id === null || restaurant_id === undefined}>{t("ui.components.selectableCustom.verPlanos")}</ModernButton>
          </ModalButtons>
        </ModalContent>
      </CustomModal>
    </>;
}
export default CustomSelect;
